/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { useParams } from "react-router"

import { ApplicationViewModel } from "../../features/application/application-viewmodel"
import { ApplicationDetailViewModel } from "../../features/application-detail/application-detail-viewmodel"
import { useViewModel, useViewModelWithArgs } from "../app/app-hooks"
import { ApplicationBreadcrumb } from "../components/application-breadcrumb"
import { ApplicationSearch } from "../components/application-search"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../primitives/tabs"
import { ApplicationDetailEvents } from "./application-detail-events"
import { ApplicationDetailFlagHistory } from "./application-detail-flag-history"
import { ApplicationDetailInfo } from "./application-detail-info"
import { ApplicationDetailMalware } from "./application-detail-malware"

export const ApplicationDetailView = observer(() => {
    const { id } = useParams<{ id: string }>()
    const vm = useViewModelWithArgs(ApplicationDetailViewModel, id)
    const appVM = useViewModel(ApplicationViewModel)

    return (
        <div className="w-content max-inline-7xl">
            <header className="mb-6 flex flex-wrap items-start justify-between gap-y-4">
                <div className="grid gap-2">
                    <h1 className="m-0 text-3xl font-bold text-primary">Device Detail</h1>
                    <ApplicationBreadcrumb isDetail selectedPageId={appVM.selectedPageId} />
                </div>
                <ApplicationSearch />
            </header>

            {vm.applicationDetail && (
                <>
                    <ApplicationDetailInfo detail={vm.applicationDetail} />

                    <Tabs defaultValue="events" className="mt-8 gap-0">
                        <TabsList variant="line" className="*:uppercase">
                            <TabsTrigger value="events">Threat Events</TabsTrigger>
                            <TabsTrigger value="malware">Mobile Malware</TabsTrigger>
                            <TabsTrigger value="flags">Flag History</TabsTrigger>
                        </TabsList>
                        {/*
                         * Keep every tab at least viewport height so switching to a shorter tab does
                         * not shrink the page below the current scroll position and cause a jump.
                         */}
                        <TabsContent value="events" className="min-h-screen">
                            <ApplicationDetailEvents vm={vm} />
                        </TabsContent>
                        <TabsContent value="malware" className="min-h-screen">
                            <ApplicationDetailMalware
                                apks={vm.applicationApks}
                                removedApks={vm.removedApplicationApks}
                            />
                        </TabsContent>
                        <TabsContent value="flags" className="min-h-screen">
                            <ApplicationDetailFlagHistory vm={vm} />
                        </TabsContent>
                    </Tabs>
                </>
            )}
        </div>
    )
})
